import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Bell, CheckCheck } from 'lucide-react';
import { useApiCaller } from '../utils/ApiUtilities';
import { useUser } from '../contexts/UserContext';
import { useNotifications } from '../contexts/NotificationContext';
import {
    getNotificationsUrl,
    getNotificationReadUrl,
    getNotificationReadAllUrl,
} from '../utils/UrlUtilities';
import {
    NOTIFICATION_TYPE_LABELS,
    itemsPerPageOptions,
    defaultPageSize,
} from '../utils/Constants';
import { formatBdPst } from '../utils/DateUtilities';
import './components.css';

// Turn a Date into the ISO string the API expects for created_at range filters.
const toIso = (d) => (d ? d.toISOString() : undefined);

export default function MyNotifications() {
    const CallApi = useApiCaller();
    const navigate = useNavigate();
    const { userState } = useUser();
    const { refreshUnread } = useNotifications();

    const [notifications, setNotifications] = useState([]);
    const [total, setTotal] = useState(0);
    const [unreadCount, setUnreadCount] = useState(0);
    const [selectedId, setSelectedId] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [loading, setLoading] = useState(false);

    // Filters
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [unreadOnly, setUnreadOnly] = useState(false);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const selected = useMemo(
        () => notifications.find((n) => n.notification_id === selectedId) || null,
        [notifications, selectedId]
    );

    const fetchNotifications = useCallback(async () => {
        setLoading(true);
        const params = { page: currentPage, limit: pageSize };
        if (appliedSearch) params.search = appliedSearch;
        if (dateFrom) params.date_from = toIso(dateFrom);
        if (dateTo) params.date_to = toIso(dateTo);
        if (unreadOnly) params.unread_only = true;

        const res = await CallApi({ url: getNotificationsUrl(params), method: 'GET', token: true });
        if (res.success) {
            setNotifications(res.data?.notifications || []);
            setTotal(res.data?.total || 0);
            setUnreadCount(res.data?.unread_count || 0);
        } else {
            setNotifications([]);
            setTotal(0);
        }
        setLoading(false);
    }, [CallApi, currentPage, pageSize, appliedSearch, dateFrom, dateTo, unreadOnly]);

    useEffect(() => {
        if (userState?.isLoggedIn) fetchNotifications();
    }, [userState?.isLoggedIn, fetchNotifications]);

    const openNotification = async (n) => {
        setSelectedId(n.notification_id);
        if (n.is_read) return;
        const res = await CallApi({
            url: getNotificationReadUrl(n.notification_id),
            method: 'POST',
            token: true,
        });
        if (res.success) {
            // Flip locally so the row shades to "read" without a refetch.
            setNotifications((prev) =>
                prev.map((item) =>
                    item.notification_id === n.notification_id
                        ? { ...item, is_read: true, read_at: new Date().toISOString() }
                        : item
                )
            );
            setUnreadCount(res.data?.unread_count ?? Math.max(0, unreadCount - 1));
            refreshUnread(); // sync the navbar badge immediately
        }
    };

    const markAllRead = async () => {
        const res = await CallApi({ url: getNotificationReadAllUrl(), method: 'POST', token: true });
        if (res.success) {
            setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
            setUnreadCount(0);
            refreshUnread();
        }
    };

    const applyFilters = () => {
        setAppliedSearch(search);
        setCurrentPage(1);
    };
    const clearFilters = () => {
        setSearch('');
        setAppliedSearch('');
        setDateFrom(null);
        setDateTo(null);
        setUnreadOnly(false);
        setCurrentPage(1);
    };

    if (!userState?.isLoggedIn) {
        return (
            <div className="container py-5 text-center" style={{ minHeight: '60vh' }}>
                <h4 className="text-muted">Please log in to view your notifications.</h4>
            </div>
        );
    }

    return (
        <div className="container py-4" style={{ minHeight: '70vh' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="fw-bold mb-0" style={{ color: '#4c1d95' }}>
                    <Bell size={24} className="me-2" />
                    My Notifications
                    {unreadCount > 0 && (
                        <span className="badge bg-danger rounded-pill ms-2">{unreadCount} unread</span>
                    )}
                </h2>
                <button
                    className="btn btn-outline-primary btn-sm rounded-pill"
                    onClick={markAllRead}
                    disabled={unreadCount === 0}
                >
                    <CheckCheck size={16} className="me-1" /> Mark all read
                </button>
            </div>

            {/* Filters */}
            <div className="card mb-3">
                <div className="card-body d-flex flex-wrap align-items-end gap-2">
                    <div>
                        <label className="form-label text-muted small mb-0">Search</label>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            value={search}
                            placeholder="title / text"
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                        />
                    </div>
                    <div>
                        <label className="form-label text-muted small mb-0 d-block">From</label>
                        <DatePicker
                            selected={dateFrom}
                            onChange={setDateFrom}
                            className="form-control form-control-sm"
                            placeholderText="From date"
                            dateFormat="yyyy-MM-dd"
                            isClearable
                        />
                    </div>
                    <div>
                        <label className="form-label text-muted small mb-0 d-block">To</label>
                        <DatePicker
                            selected={dateTo}
                            onChange={setDateTo}
                            className="form-control form-control-sm"
                            placeholderText="To date"
                            dateFormat="yyyy-MM-dd"
                            isClearable
                        />
                    </div>
                    <div className="form-check ms-2 mb-1">
                        <input
                            id="unreadOnly"
                            type="checkbox"
                            className="form-check-input"
                            checked={unreadOnly}
                            onChange={(e) => {
                                setUnreadOnly(e.target.checked);
                                setCurrentPage(1);
                            }}
                        />
                        <label htmlFor="unreadOnly" className="form-check-label small">Unread only</label>
                    </div>
                    <button className="btn btn-primary btn-sm rounded-pill" onClick={applyFilters}>Apply</button>
                    <button className="btn btn-outline-secondary btn-sm rounded-pill" onClick={clearFilters}>Clear</button>
                </div>
            </div>

            {/* Two-pane: list (left) + detail (right) */}
            <div className="row g-3">
                <div className="col-md-5">
                    {loading ? (
                        <div className="text-center p-4">
                            <div className="spinner-border text-primary" role="status" />
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="alert alert-light border text-center">No notifications.</div>
                    ) : (
                        <div className="list-group">
                            {notifications.map((n) => (
                                <button
                                    key={n.notification_id}
                                    type="button"
                                    className={`list-group-item list-group-item-action text-start${
                                        selectedId === n.notification_id ? ' active' : ''
                                    }`}
                                    style={
                                        selectedId === n.notification_id
                                            ? undefined
                                            : {
                                                  background: n.is_read ? '#fff' : '#f5f3ff',
                                                  borderLeft: n.is_read ? '3px solid transparent' : '3px solid #7c3aed',
                                              }
                                    }
                                    onClick={() => openNotification(n)}
                                >
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span style={{ fontWeight: n.is_read ? 400 : 700 }}>{n.title}</span>
                                        <span className="badge bg-secondary-subtle text-dark ms-2">
                                            {NOTIFICATION_TYPE_LABELS[n.notification_type] || n.notification_type}
                                        </span>
                                    </div>
                                    {n.summary && <div className="small text-muted">{n.summary}</div>}
                                    <div className="small text-muted mt-1">{formatBdPst(n.created_at)}</div>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex align-items-center gap-2">
                            <label htmlFor="notifPageSize" className="form-label text-muted mb-0 small">Per page:</label>
                            <select
                                id="notifPageSize"
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                className="form-select form-select-sm w-auto"
                            >
                                {itemsPerPageOptions.map((size) => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </select>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <button
                                className="btn btn-outline-secondary btn-sm rounded-pill"
                                disabled={currentPage <= 1}
                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            >
                                Prev
                            </button>
                            <span className="text-muted small">Page {currentPage} of {totalPages}</span>
                            <button
                                className="btn btn-outline-secondary btn-sm rounded-pill"
                                disabled={currentPage >= totalPages}
                                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>

                <div className="col-md-7">
                    <div className="card h-100">
                        <div className="card-body">
                            {selected ? (
                                <>
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <h4 className="fw-bold mb-0">{selected.title}</h4>
                                        <span className="badge bg-secondary-subtle text-dark">
                                            {NOTIFICATION_TYPE_LABELS[selected.notification_type] || selected.notification_type}
                                        </span>
                                    </div>
                                    <div className="text-muted small mb-3">{formatBdPst(selected.created_at)}</div>
                                    {selected.summary && <p className="fw-semibold">{selected.summary}</p>}
                                    <p style={{ whiteSpace: 'pre-line' }}>{selected.body}</p>
                                    {selected.notification_type === 'COMPETITION_RESULT' && selected.metadata?.quiz_id && (
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => {
                                                const lvl = encodeURIComponent(selected.metadata.level || '');
                                                navigate(`/userquiz?view=COMPETITION_QUIZ&level=${lvl}&quiz=${selected.metadata.quiz_id}&show=leaderboard`);
                                            }}
                                        >
                                            🏆 View published result
                                        </button>
                                    )}
                                </>
                            ) : (
                                <div className="text-muted text-center py-5">
                                    Select a notification to read it.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
